import { BaseModal } from "@/components/modal/base-modal";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { chevronDownOutline } from "ionicons/icons";
import { ReactNode, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

type ModalSelectOption = {
  value: string;
  name: string;
};

interface ModalSelectProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  placeholder?: string;
  options: ModalSelectOption[];
  disabled?: boolean;
  className?: string;
  initialBreakpoint?: number;
  breakpoints?: number[];
  theme?: "light" | "settings";
  renderModalContent?: (context: {
    value: unknown;
    options: ModalSelectOption[];
    onSelect: (value: string) => void;
    close: () => void;
  }) => ReactNode;
}

function formatDisplayValue(value: unknown, options: ModalSelectOption[]) {
  if (value === undefined || value === null || value === "") return "";
  const found = options.find((option) => option.value === value);
  return found?.name ?? String(value);
}

export default function ModalSelect<T extends FieldValues>({
  control,
  name,
  placeholder,
  options,
  disabled = false,
  className,
  initialBreakpoint,
  breakpoints,
  theme = "light",
  renderModalContent,
}: ModalSelectProps<T>) {
  const isSettings = theme === "settings";
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState<string | null>(null);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const currentValue = tempValue ?? field.value;
        const displayValue = formatDisplayValue(field.value, options);

        const handleSelect = (value: string) => {
          field.onChange(value);
          setIsOpen(false);
        };

        return (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setTempValue(field.value ?? null);
                setIsOpen(true);
              }}
              className={cn(
                "h-11.5 w-full rounded-xl border border-[var(--color-border-input-default)] bg-white px-3 text-left text-base",
                "flex items-center justify-between",
                "transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60",
                isSettings &&
                  "h-[52px] rounded-[27px] border-[#ebe0fb] px-[18px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)]",
                className,
              )}
            >
              <span className={cn(isSettings && textVariants.inputText, displayValue ? "text-black" : "text-text-placeholder-default")}>
                {displayValue || placeholder || "Choose..."}
              </span>
              <IonIcon
                icon={chevronDownOutline}
                className={cn("transition-transform duration-150", {
                  "rotate-180": isOpen,
                })}
              />
            </button>

            <BaseModal
              isOpen={isOpen}
              onDismiss={() => setIsOpen(false)}
              initialBreakpoint={initialBreakpoint}
              breakpoints={breakpoints}
              scrollY={false}
              forceOverscroll={false}
              styles={{
                content: {
                  overflow: "hidden",
                },
              }}
            >
              {renderModalContent ? (
                renderModalContent({
                  value: currentValue,
                  options,
                  onSelect: (value) => handleSelect(value),
                  close: () => setIsOpen(false),
                })
              ) : (
                <>
                  <IonHeader>
                    <IonToolbar>
                      <IonTitle>{placeholder || "Select an option"}</IonTitle>
                      <IonButton slot="end" fill="clear" onClick={() => setIsOpen(false)}>
                        Close
                      </IonButton>
                    </IonToolbar>
                  </IonHeader>
                  <IonContent>
                    <IonList>
                      {options.map((option) => (
                        <IonItem
                          button
                          key={option.value}
                          onClick={() => handleSelect(option.value)}
                        >
                          {option.name}
                        </IonItem>
                      ))}
                    </IonList>
                  </IonContent>
                </>
              )}
            </BaseModal>
          </>
        );
      }}
    />
  );
}
